import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { adminAPI, API_ORIGIN } from '../../../services/api';
import { FiSave, FiPlus, FiTrash2, FiEdit3, FiCheckCircle, FiFileText, FiList, FiBook } from 'react-icons/fi';
import { BsPlayCircle } from 'react-icons/bs';
import ttsService from '../../../utils/textToSpeech';
import { parseSentencesFromFullText } from '../../../utils/lessonTextSentences';
import '../Students/StudentsPage.css';

const CreateLessonPage = () => {
    const { lessonId } = useParams();
    const isEditMode = !!lessonId;
    
    const [areas, setAreas] = useState([]);
    const [levels, setLevels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Шаг 1: Выбор базовых параметров
    const [category, setCategory] = useState('grammar');
    const [selectedArea, setSelectedArea] = useState('');
    const [selectedLevel, setSelectedLevel] = useState('');
    
    // Шаг 2: Данные урока
    const [lessonTitle, setLessonTitle] = useState('');
    const [lessonDescription, setLessonDescription] = useState('');
    
    // Шаг 3: Отдельные предложения для прослушивания
    const [standaloneSentences, setStandaloneSentences] = useState([
        { en: '', ru: '', parts: [] }
    ]);
    
    // Шаг 4: Текст
    const [textTitle, setTextTitle] = useState('');
    const [lessonTextId, setLessonTextId] = useState(null);
    const [textDescription, setTextDescription] = useState('');
    const [textImageUrl, setTextImageUrl] = useState('');
    const [textImageFile, setTextImageFile] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [fullTextEn, setFullTextEn] = useState('');
    const [fullTextRu, setFullTextRu] = useState('');
    const [textSentences, setTextSentences] = useState([]);
    const [textVoiceId, setTextVoiceId] = useState('en-US-JennyNeural');
    const [textAudioType, setTextAudioType] = useState('auto'); // 'auto' или 'custom'
    const [textAudioFile, setTextAudioFile] = useState(null);
    const [textAudioUrl, setTextAudioUrl] = useState('');
    const [uploadingTextAudio, setUploadingTextAudio] = useState(false);
    
    // Состояние для превью аудио
    const [isPlayingPreview, setIsPlayingPreview] = useState(false);
    
    // Шаг 5: Упражнения
    const [exercises, setExercises] = useState([]);
    
    // Шаг 6: Видео (опционально)
    const [videos, setVideos] = useState([]);
    
    // Голоса для озвучки
    const [availableVoices, setAvailableVoices] = useState([]);

    const [currentStep, setCurrentStep] = useState(1);

    useEffect(() => {
        const initializeData = async () => {
            await loadInitialData();
            await loadVoices();
        };
        initializeData();
    }, []);
    
    useEffect(() => {
        // Загружаем урок только после того, как загрузились области и уровни
        if (isEditMode && areas.length > 0 && levels.length > 0) {
            loadLessonForEdit();
        }
    }, [isEditMode, lessonId, areas.length, levels.length]);

    const loadInitialData = async () => {
        try {
            const [areasRes, levelsRes] = await Promise.all([
                adminAPI.getAreas(),
                adminAPI.getLevels()
            ]);
            setAreas(areasRes.data.areas);
            setLevels(levelsRes.data.levels);
        } catch (err) {
            setError('Ошибка загрузки данных');
        }
    };

    const loadVoices = async () => {
        try {
            const response = await fetch(`${API_ORIGIN}/api/tts/voices`);
            const data = await response.json();
            setAvailableVoices(data.voices || []);
        } catch (error) {
            console.error('Ошибка загрузки голосов:', error);
            setAvailableVoices([]);
        }
    };

    const loadLessonForEdit = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getLesson(lessonId);
            const lesson = response.data;
            
            console.log('Загружен урок для редактирования:', lesson);
            console.log('Доступные уровни:', levels);
            console.log('Доступные области:', areas);
            
            // Загружаем основные данные
            setCategory(lesson.category || 'grammar');
            setSelectedLevel(String(lesson.level_id) || '');
            setLessonTitle(lesson.title || '');
            setLessonDescription(lesson.description || '');
            
            // Важно: загружаем область через уровень ИЛИ напрямую из lesson.area_id
            if (lesson.area_id) {
                setSelectedArea(String(lesson.area_id));
            } else if (lesson.level_id) {
                const level = levels.find(l => l.id === lesson.level_id);
                if (level) {
                    setSelectedArea(String(level.area_id));
                }
            }
            
            // Загружаем отдельные предложения
            if (lesson.standaloneSentences && lesson.standaloneSentences.length > 0) {
                setStandaloneSentences(lesson.standaloneSentences.map(s => {
                    let parts = [];
                    let partsRu = [];
                    
                    // Проверяем наличие разделителей в тексте
                    const enText = s.sentence_en || '';
                    const ruText = s.sentence_ru || '';
                    const enParts = enText.split('|').map(p => p.trim()).filter(p => p);
                    const ruParts = ruText.split('|').map(p => p.trim()).filter(p => p);
                    
                    // Если есть разделители и количество совпадает
                    if (enParts.length > 1 && ruParts.length > 1 && enParts.length === ruParts.length) {
                        parts = enParts;
                        partsRu = ruParts;
                    } else {
                        // Иначе пытаемся загрузить из semantic_parts (для старых данных)
                        try {
                            if (s.semantic_parts && s.semantic_parts !== '[]') {
                                const parsedParts = JSON.parse(s.semantic_parts);
                                if (parsedParts.length > 0) {
                                    // Если есть старые части, восстанавливаем их с разделителями
                                    parts = parsedParts;
                                    // Если части есть, но в тексте нет разделителей, добавляем их
                                    if (!enText.includes('|') && parts.length > 1) {
                                        s.sentence_en = parts.join(' | ');
                                    }
                                }
                            }
                        } catch (e) {
                            console.error('Ошибка парсинга semantic_parts:', e);
                        }
                    }
                    
                    return {
                        en: enText,
                        ru: ruText,
                        parts: parts,
                        partsRu: partsRu,
                        partsValid: parts.length === 0 || (parts.length === ruParts.length),
                        voiceId: s.voice_id || 'en-US-JennyNeural',
                        audioType: s.audio_url ? 'custom' : 'auto',
                        audioUrl: s.audio_url || '',
                        audioFile: null
                    };
                }));
            }
            
            // Загружаем тексты и упражнения
            if (lesson.texts && lesson.texts.length > 0) {
                const firstText = lesson.texts[0];
                setLessonTextId(firstText.id != null ? firstText.id : null);
                setTextTitle(firstText.title || '');
                setTextDescription(firstText.description || '');
                setTextImageUrl(firstText.image_url || '');
                setTextVoiceId(firstText.voice_id || 'en-US-JennyNeural');
                setTextAudioType(firstText.audio_url ? 'custom' : 'auto');
                setTextAudioUrl(firstText.audio_url || '');
                setFullTextEn(firstText.full_text_en || '');
                setFullTextRu(firstText.full_text_ru || '');
                
                // Автоматически разбиваем текст на предложения
                if (firstText.full_text_en) {
                    const sentences = parseSentencesFromFullText(firstText.full_text_en);
                    const sentencesRu = firstText.full_text_ru ? parseSentencesFromFullText(firstText.full_text_ru) : [];
                    
                    // Если есть предложения из БД, используем их (с audio_url)
                    if (firstText.sentences && firstText.sentences.length > 0) {
                        setTextSentences(firstText.sentences.map(s => {
                            let parts = [];
                            let partsRu = [];
                            let partsValid = true;
                            
                            // Проверяем наличие разделителей в тексте
                            const enText = s.sentence_en || '';
                            const ruText = s.sentence_ru || '';
                            const enParts = enText.split('|').map(p => p.trim()).filter(p => p);
                            const ruParts = ruText.split('|').map(p => p.trim()).filter(p => p);
                            
                            // Если есть разделители и количество совпадает
                            if (enParts.length > 1 && ruParts.length > 1 && enParts.length === ruParts.length) {
                                parts = enParts;
                                partsRu = ruParts;
                                partsValid = true;
                            } else if (enParts.length > 1 || ruParts.length > 1) {
                                // Количество не совпадает
                                parts = enParts;
                                partsRu = ruParts;
                                partsValid = false;
                            }
                            
                            // Загружаем аудио частей если есть
                            let partsAudio = [];
                            try {
                                if (s.parts_audio) {
                                    const parsedPartsAudio = typeof s.parts_audio === 'string' 
                                        ? JSON.parse(s.parts_audio) 
                                        : s.parts_audio;
                                    partsAudio = parsedPartsAudio.map(url => url ? { audioUrl: url } : null);
                                }
                            } catch (e) {
                                console.error('Ошибка парсинга parts_audio:', e);
                            }
                            
                            // Если parts_audio еще не инициализирован, но есть части
                            if (parts.length > 1 && partsAudio.length === 0) {
                                partsAudio = new Array(parts.length).fill(null);
                            }
                            
                            return {
                                en: enText,
                                ru: ruText,
                                parts: parts,
                                partsRu: partsRu,
                                partsValid: partsValid,
                                partsAudio: partsAudio,
                                audioType: s.audio_url ? 'custom' : 'auto',
                                audioUrl: s.audio_url || '',
                                audioFile: null
                            };
                        }));
                    } else {
                        // Иначе создаем предложения из текста
                        setTextSentences(sentences.map((s, i) => ({
                            en: s.trim(),
                            ru: sentencesRu[i] ? sentencesRu[i].trim() : '',
                            parts: [],
                            partsRu: [],
                            partsValid: true,
                            audioType: 'auto',
                            audioUrl: '',
                            audioFile: null
                        })));
                    }
                }
                
                // Загружаем упражнения с вопросами
                if (firstText.exercises && firstText.exercises.length > 0) {
                    console.log('Загружаем упражнения:', firstText.exercises);
                    setExercises(firstText.exercises.map(ex => {
                        console.log('Упражнение:', ex);
                        console.log('Вопросы упражнения:', ex.questions);
                        
                        return {
                            id: ex.id,
                            title: ex.title || '',
                            instructions: ex.instructions || '',
                            type: ex.exercise_type || 'multiple_choice',
                            questions: (ex.questions || []).map(q => {
                                console.log('Вопрос:', q);
                                
                                const exerciseType = ex.exercise_type || 'multiple_choice';
                                
                                // Парсим question_data
                                let parsedData = [];
                                try {
                                    if (q.question_data) {
                                        const parsed = typeof q.question_data === 'string' 
                                            ? JSON.parse(q.question_data) 
                                            : q.question_data;
                                        parsedData = Array.isArray(parsed) ? parsed : [];
                                    }
                                } catch (e) {
                                    console.error('Ошибка парсинга question_data:', e);
                                    parsedData = [];
                                }
                                
                                // Парсим correct_answer
                                let correctAnswer = '';
                                try {
                                    if (q.correct_answer) {
                                        if (typeof q.correct_answer === 'string') {
                                            correctAnswer = q.correct_answer.startsWith('"') && q.correct_answer.endsWith('"') 
                                                ? q.correct_answer.slice(1, -1) 
                                                : q.correct_answer;
                                        } else {
                                            correctAnswer = String(q.correct_answer);
                                        }
                                    }
                                } catch (e) {
                                    console.error('Ошибка обработки correct_answer:', e);
                                    correctAnswer = '';
                                }
                                
                                // Формируем объект вопроса в зависимости от типа
                                const questionObj = {
                                    questionText: q.question_text || '',
                                    explanation: q.explanation || ''
                                };
                                
                                if (exerciseType === 'multiple_choice') {
                                    questionObj.options = parsedData;
                                    questionObj.correctAnswer = correctAnswer;
                                } else if (exerciseType === 'match') {
                                    // Для сопоставления parsedData содержит пары {left, right}
                                    questionObj.matchPairs = parsedData.length > 0 ? parsedData : [];
                                } else {
                                    // Для text_answer и fill_gaps
                                    questionObj.correctAnswer = correctAnswer;
                                }
                                
                                console.log('Обработанный вопрос:', questionObj);
                                
                                return questionObj;
                            })
                        };
                    }));
                }
            } else {
                setLessonTextId(null);
            }
            
            // Загружаем видео
            if (lesson.videos && lesson.videos.length > 0) {
                setVideos(lesson.videos.map(v => ({
                    title: v.title || '',
                    description: v.description || '',
                    url: v.video_url || ''
                })));
            }
            
            setSuccess(`Урок "${lesson.title}" загружен для редактирования`);
        } catch (error) {
            console.error('Ошибка загрузки урока:', error);
            setError('Не удалось загрузить урок для редактирования');
        } finally {
            setLoading(false);
        }
    };

    const splitTextIntoSentences = () => {
        if (!fullTextEn) return;
        
        const sentences = fullTextEn.match(/[^.!?]+[.!?]+/g) || [fullTextEn];
        const sentencesRu = fullTextRu ? (fullTextRu.match(/[^.!?]+[.!?]+/g) || [fullTextRu]) : [];
        
        const parsed = sentences.map((s, i) => {
            const enSentence = s.trim();
            const ruSentence = sentencesRu[i] ? sentencesRu[i].trim() : '';
            
            // Проверяем наличие разделителей в предложении
            const enParts = enSentence.split('|').map(p => p.trim()).filter(p => p);
            const ruParts = ruSentence.split('|').map(p => p.trim()).filter(p => p);
            
            // Определяем части и их валидность
            let parts = [];
            let partsRu = [];
            let partsValid = true;
            
            // Если в английском или русском есть разделители
            if (enSentence.includes('|') || ruSentence.includes('|')) {
                if (enParts.length === ruParts.length) {
                    parts = enParts;
                    partsRu = ruParts;
                    partsValid = true;
                } else {
                    // Количество не совпадает - ошибка
                    parts = enParts;
                    partsRu = ruParts;
                    partsValid = false;
                }
            }
            
            return {
                en: enSentence,
                ru: ruSentence,
                parts: parts,
                partsRu: partsRu,
                partsValid: partsValid,
                partsAudio: parts.length > 1 ? new Array(parts.length).fill(null) : [], // инициализируем массив для аудио частей
                audioType: 'auto', // по умолчанию автоматическая озвучка
                audioUrl: '',
                audioFile: null
            };
        });
        
        // Проверяем, есть ли ошибки валидации
        const hasErrors = parsed.some(p => p.partsValid === false);
        
        if (hasErrors) {
            setError('⚠️ Внимание: в некоторых предложениях количество разделителей | не совпадает!');
        } else {
            setError('');
            setSuccess('✓ Текст разбит на ' + parsed.length + ' предложений');
        }
        
        setTextSentences(parsed);
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Проверка типа файла
        if (!file.type.startsWith('image/')) {
            setError('Пожалуйста, выберите файл изображения');
            return;
        }

        // Проверка размера (максимум 8 МБ)
        if (file.size > 8 * 1024 * 1024) {
            setError('Размер файла не должен превышать 8 МБ');
            return;
        }

        // Создаем локальное превью сразу
        const previewUrl = URL.createObjectURL(file);
        setTextImageUrl(previewUrl);
        setTextImageFile(file);

        setUploadingImage(true);
        setError('');

        try {
            const formData = new FormData();
            formData.append('image', file);

            const response = await adminAPI.uploadTextImage(formData);
            
            // Освобождаем временный URL
            URL.revokeObjectURL(previewUrl);
            
            // Устанавливаем серверный URL
            setTextImageUrl(response.data.imageUrl);
            setTextImageFile(null);
            setSuccess('Изображение успешно загружено');
        } catch (err) {
            setError('Ошибка при загрузке изображения');
            console.error(err);
            
            // При ошибке очищаем превью
            URL.revokeObjectURL(previewUrl);
            setTextImageUrl('');
            setTextImageFile(null);
        } finally {
            setUploadingImage(false);
        }
    };

    const handleRemoveImage = () => {
        setTextImageUrl('');
        setTextImageFile(null);
    };

    // Загрузка аудио для standalone предложения
    const handleSentenceAudioUpload = async (index, file) => {
        if (!file) return;

        if (!file.type.startsWith('audio/')) {
            setError('Пожалуйста, выберите аудио файл');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            setError('Размер аудио файла не должен превышать 10MB');
            return;
        }

        try {
            const formData = new FormData();
            formData.append('audio', file);

            const response = await adminAPI.uploadSentenceAudio(formData);
            
            const updated = [...standaloneSentences];
            updated[index].audioUrl = response.data.audioUrl;
            updated[index].audioFile = null;
            setStandaloneSentences(updated);
            
            setSuccess('Аудио успешно загружено');
        } catch (error) {
            console.error('Ошибка загрузки аудио:', error);
            setError('Ошибка при загрузке аудио файла');
        }
    };

    // Удаление аудио для standalone предложения
    const handleRemoveSentenceAudio = (index) => {
        const updated = [...standaloneSentences];
        updated[index].audioUrl = '';
        updated[index].audioFile = null;
        setStandaloneSentences(updated);
    };

    // Загрузка аудио для полного текста
    const handleTextAudioUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('audio/')) {
            setError('Пожалуйста, выберите аудио файл');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            setError('Размер аудио файла не должен превышать 10MB');
            return;
        }

        setUploadingTextAudio(true);
        setError('');

        try {
            const formData = new FormData();
            formData.append('audio', file);

            const response = await adminAPI.uploadTextAudio(formData);
            setTextAudioUrl(response.data.audioUrl);
            setTextAudioFile(null);
            setSuccess('Аудио успешно загружено');
        } catch (error) {
            console.error('Ошибка загрузки аудио:', error);
            setError('Ошибка при загрузке аудио файла');
        } finally {
            setUploadingTextAudio(false);
        }
    };

    // Удаление аудио текста
    const handleRemoveTextAudio = () => {
        setTextAudioUrl('');
        setTextAudioFile(null);
    };

    // Загрузка аудио для предложения текста
    const handleTextSentenceAudioUpload = async (index, file) => {
        if (!file) return;

        if (!file.type.startsWith('audio/')) {
            setError('Пожалуйста, выберите аудио файл');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            setError('Размер аудио файла не должен превышать 10MB');
            return;
        }

        try {
            const formData = new FormData();
            formData.append('audio', file);

            const response = await adminAPI.uploadSentenceAudio(formData);
            
            const updated = [...textSentences];
            updated[index].audioUrl = response.data.audioUrl;
            updated[index].audioFile = null;
            setTextSentences(updated);
            
            setSuccess('Аудио успешно загружено');
        } catch (error) {
            console.error('Ошибка загрузки аудио:', error);
            setError('Ошибка при загрузке аудио файла');
        }
    };

    const handleTextSentencePartAudioUpload = async (sentenceIndex, partIndex, file) => {
        if (!file) return;

        if (!file.type.startsWith('audio/')) {
            setError('Пожалуйста, выберите аудио файл');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            setError('Размер аудио файла не должен превышать 10MB');
            return;
        }

        try {
            const formData = new FormData();
            formData.append('audio', file);

            const response = await adminAPI.uploadSentenceAudio(formData);
            
            const updated = [...textSentences];
            if (!updated[sentenceIndex].partsAudio) {
                updated[sentenceIndex].partsAudio = [];
            }
            updated[sentenceIndex].partsAudio[partIndex] = {
                audioUrl: response.data.audioUrl
            };
            setTextSentences(updated);
            
            setSuccess(`Аудио для части ${partIndex + 1} успешно загружено`);
        } catch (error) {
            console.error('Ошибка загрузки аудио:', error);
            setError('Ошибка при загрузке аудио файла');
        }
    };

    const addStandaloneSentence = () => {
        setStandaloneSentences([...standaloneSentences, { 
            en: '', 
            ru: '', 
            parts: [], 
            voiceId: 'en-US-JennyNeural',
            audioType: 'auto', // 'auto' или 'custom'
            audioFile: null,
            audioUrl: ''
        }]);
    };

    const updateStandaloneSentence = (index, field, value) => {
        const updated = [...standaloneSentences];
        updated[index][field] = value;
        
        // Автоматически определяем части из разделителей в тексте
        if (field === 'en' || field === 'ru') {
            const enParts = updated[index].en.split('|').map(p => p.trim()).filter(p => p);
            const ruParts = updated[index].ru.split('|').map(p => p.trim()).filter(p => p);
            
            // Сохраняем части только если они есть в обоих языках и их количество совпадает
            if (enParts.length > 1 && ruParts.length > 1 && enParts.length === ruParts.length) {
                updated[index].parts = enParts;
                updated[index].partsRu = ruParts;
                updated[index].partsValid = true;
            } else if (enParts.length > 1 || ruParts.length > 1) {
                // Есть разделители, но количество не совпадает
                updated[index].parts = [];
                updated[index].partsRu = [];
                updated[index].partsValid = false;
            } else {
                // Нет разделителей
                updated[index].parts = [];
                updated[index].partsRu = [];
                updated[index].partsValid = true;
            }
        }
        
        setStandaloneSentences(updated);
    };

    const removeStandaloneSentence = (index) => {
        setStandaloneSentences(standaloneSentences.filter((_, i) => i !== index));
    };

    // Функция для воспроизведения предложения
    const playPreviewSentence = (text, voiceId) => {
        if (!text) {
            alert('Сначала введите текст предложения');
            return;
        }
        
        // Убираем разделители для воспроизведения
        const cleanText = text.replace(/\s*\|\s*/g, ' ').trim();
        
        setIsPlayingPreview(true);
        
        ttsService.speak(cleanText, {
            rate: 1,
            lang: 'en-US',
            voice: voiceId || 'en-US-JennyNeural'
        }).then(() => {
            setIsPlayingPreview(false);
        }).catch(error => {
            console.error('Ошибка воспроизведения:', error);
            alert('Ошибка воспроизведения. Проверьте подключение к серверу.');
            setIsPlayingPreview(false);
        });
    };

    const stopPreviewAudio = () => {
        ttsService.cancel();
        setIsPlayingPreview(false);
    };

    const addExercise = (type) => {
        const newExercise = {
            type,
            title: '',
            instructions: '',
            questions: [{ questionText: '', options: [], correctAnswer: '', explanation: '' }]
        };
        setExercises([...exercises, newExercise]);
    };

    const updateExercise = (exIndex, field, value) => {
        const updated = [...exercises];
        updated[exIndex][field] = value;
        setExercises(updated);
    };

    const addQuestionToExercise = (exIndex) => {
        const updated = [...exercises];
        const exerciseType = updated[exIndex].type;
        
        // Инициализируем вопрос в зависимости от типа упражнения
        const newQuestion = {
            questionText: '',
            options: exerciseType === 'multiple_choice' ? [] : undefined,
            matchPairs: exerciseType === 'match' ? [] : undefined,
            correctAnswer: exerciseType !== 'match' ? '' : undefined,
            explanation: ''
        };
        
        updated[exIndex].questions.push(newQuestion);
        setExercises(updated);
    };

    const updateQuestion = (exIndex, qIndex, field, value) => {
        const updated = [...exercises];
        updated[exIndex].questions[qIndex][field] = value;
        setExercises(updated);
    };

    const removeExercise = (index) => {
        setExercises(exercises.filter((_, i) => i !== index));
    };

    const addVideo = () => {
        setVideos([...videos, { title: '', url: '', type: 'youtube' }]);
    };

    const updateVideo = (index, field, value) => {
        const updated = [...videos];
        updated[index][field] = value;
        setVideos(updated);
    };

    const removeVideo = (index) => {
        setVideos(videos.filter((_, i) => i !== index));
    };

    const handleSaveLesson = async () => {
        try {
            setLoading(true);
            setError('');
            
            const lessonData = {
                category,
                areaId: selectedArea,
                levelId: selectedLevel,
                lessonTitle,
                lessonDescription,
                standaloneSentences: standaloneSentences.filter(s => s.en),
                textTitle,
                textDescription,
                textImageUrl,
                textVoiceId,
                textAudioType,
                textAudioUrl,
                fullTextEn,
                fullTextRu,
                textSentences,
                textId: lessonTextId,
                exercises: exercises.map(ex => ({
                    ...ex,
                    questions: ex.questions.filter(q => q.questionText).map(q => {
                        // Преобразуем данные вопроса в зависимости от типа упражнения
                        if (ex.type === 'match') {
                            return {
                                questionText: q.questionText,
                                questionData: q.matchPairs || [],
                                correctAnswer: '', // Для match не используется
                                explanation: q.explanation || ''
                            };
                        } else if (ex.type === 'multiple_choice') {
                            return {
                                questionText: q.questionText,
                                questionData: q.options || [],
                                correctAnswer: q.correctAnswer || '',
                                explanation: q.explanation || ''
                            };
                        } else if (ex.type === 'true_false') {
                            return {
                                questionText: q.questionText,
                                questionData: ['true', 'false'], // Фиксированные варианты
                                correctAnswer: q.correctAnswer || '',
                                explanation: q.explanation || ''
                            };
                        } else {
                            return {
                                questionText: q.questionText,
                                questionData: [],
                                correctAnswer: q.correctAnswer || '',
                                explanation: q.explanation || ''
                            };
                        }
                    })
                })),
                videos: videos.filter(v => v.url)
            };

            let response;
            if (isEditMode) {
                response = await adminAPI.updateLesson(lessonId, lessonData);
                setSuccess('Урок успешно обновлен!');
            } else {
                response = await adminAPI.createComplexLesson(lessonData);
                setSuccess('Урок успешно создан! ID: ' + response.data.lessonId);
            }
            
            // Перенаправление через 2 секунды
            setTimeout(() => {
                window.location.href = '/admin/view-lessons';
            }, 2000);
            
        } catch (err) {
            setError(err.response?.data?.error || 'Ошибка при сохранении урока');
        } finally {
            setLoading(false);
        }
    };

    const canProceedToStep2 = selectedArea && selectedLevel && lessonTitle;
    const canProceedToStep3 = canProceedToStep2;
    const canProceedToStep4 = true;
    const canProceedToStep5 = fullTextEn && textSentences.length > 0;

    return (
        <div className="container">
            <div className="page-header">
                <div>
                    <h1>{isEditMode ? 'Редактирование урока' : 'Создать комплексный урок'}</h1>
                    <p>Единая форма для {isEditMode ? 'редактирования' : 'создания'} урока со всеми компонентами</p>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            {/* Вкладки категорий */}
            <div className="card" style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '10px', borderBottom: '2px solid #e8e8e8', paddingBottom: '10px' }}>
                    <button
                        className={`category-tab ${category === 'grammar' ? 'active' : ''}`}
                        onClick={() => setCategory('grammar')}
                        style={{
                            padding: '12px 24px',
                            background: category === 'grammar' ? '#4a90e2' : 'transparent',
                            color: category === 'grammar' ? 'white' : '#666',
                            border: 'none',
                            borderRadius: '8px 8px 0 0',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <FiBook /> Грамматика
                    </button>
                    <button
                        className={`category-tab ${category === 'vocabulary' ? 'active' : ''}`}
                        onClick={() => setCategory('vocabulary')}
                        style={{
                            padding: '12px 24px',
                            background: category === 'vocabulary' ? '#7b68ee' : 'transparent',
                            color: category === 'vocabulary' ? 'white' : '#666',
                            border: 'none',
                            borderRadius: '8px 8px 0 0',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <FiFileText /> Лексика
                    </button>
                </div>
            </div>

            {/* Прогресс бар */}
            <div className="card" style={{ marginBottom: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {[
                        { num: 1, label: 'Основное' },
                        { num: 2, label: 'Предложения' },
                        { num: 3, label: 'Текст' },
                        { num: 4, label: 'Упражнения' },
                        { num: 5, label: 'Видео' },
                        { num: 6, label: 'Сохранить' }
                    ].map(step => (
                        <div
                            key={step.num}
                            onClick={() => setCurrentStep(step.num)}
                            style={{
                                cursor: 'pointer',
                                textAlign: 'center',
                                opacity: currentStep === step.num ? 1 : 0.5,
                                transition: 'all 0.3s'
                            }}
                        >
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                background: currentStep >= step.num ? '#4a90e2' : '#e8e8e8',
                                color: currentStep >= step.num ? 'white' : '#999',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                margin: '0 auto 8px'
                            }}>
                                {step.num}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666' }}>{step.label}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ШАГ 1: Основные параметры */}
            {currentStep === 1 && (
                <div className="card">
                    <h2 style={{ marginBottom: '20px' }}>Шаг 1: Основные параметры урока</h2>
                    
                    <div className="form-group">
                        <label>Категория: <strong style={{ color: category === 'grammar' ? '#4a90e2' : '#7b68ee' }}>
                            {category === 'grammar' ? 'Грамматика' : 'Лексика'}
                        </strong></label>
                    </div>

                    <div className="form-group">
                        <label>Область обучения *</label>
                        <select
                            value={selectedArea}
                            onChange={(e) => setSelectedArea(e.target.value)}
                            required
                        >
                            <option value="">Выберите область</option>
                            {areas.map(area => (
                                <option key={area.id} value={area.id}>
                                    {area.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {selectedArea && (
                        <div className="form-group">
                            <label>Уровень *</label>
                            <select
                                value={selectedLevel}
                                onChange={(e) => setSelectedLevel(e.target.value)}
                                required
                            >
                                <option value="">Выберите уровень</option>
                                {levels.filter(l => l.area_id == selectedArea).map(level => (
                                    <option key={level.id} value={level.id}>
                                        {level.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="form-group">
                        <label>Название урока *</label>
                        <input
                            type="text"
                            value={lessonTitle}
                            onChange={(e) => setLessonTitle(e.target.value)}
                            placeholder="Например: Present Simple - основы"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Описание урока</label>
                        <textarea
                            value={lessonDescription}
                            onChange={(e) => setLessonDescription(e.target.value)}
                            placeholder="Краткое описание содержания урока"
                            rows="3"
                        />
                    </div>

                    <button
                        className="btn btn-primary"
                        onClick={() => setCurrentStep(2)}
                        disabled={!canProceedToStep2}
                    >
                        Далее: Предложения →
                    </button>
                </div>
            )}

            {/* ШАГ 2: Отдельные предложения */}
            {currentStep === 2 && (
                <div className="card">
                    <h2 style={{ marginBottom: '20px' }}>Шаг 2: Предложения для прослушивания</h2>
                    <p style={{ color: '#666', marginBottom: '20px' }}>
                        Добавьте отдельные предложения для тренировки.
                    </p>

                    {standaloneSentences.map((sentence, index) => (
                        <div key={index} className="card" style={{ marginBottom: '15px', background: '#f9f9f9' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                <strong>Предложение {index + 1}</strong>
                                {standaloneSentences.length > 1 && (
                                    <button
                                        className="btn-icon btn-danger"
                                        onClick={() => removeStandaloneSentence(index)}
                                    >
                                        <FiTrash2 />
                                    </button>
                                )}
                            </div>

                            <div className="form-group">
                                <label>English *</label>
                                <input
                                    type="text"
                                    value={sentence.en}
                                    onChange={(e) => updateStandaloneSentence(index, 'en', e.target.value)}
                                    placeholder="I am going to the store"
                                />
                            </div>

                            <div className="form-group">
                                <label>Русский перевод *</label>
                                <input
                                    type="text"
                                    value={sentence.ru}
                                    onChange={(e) => updateStandaloneSentence(index, 'ru', e.target.value)}
                                    placeholder="Я иду в магазин"
                                />
                            </div>

                            <div className="form-group">
                                <label>Тип озвучки</label>
                                <select
                                    value={sentence.audioType || 'auto'}
                                    onChange={(e) => updateStandaloneSentence(index, 'audioType', e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '8px',
                                        border: '1px solid #ddd',
                                        marginBottom: '10px'
                                    }}
                                >
                                    <option value="auto">Автоматическая озвучка (TTS)</option>
                                    <option value="custom">Собственное аудио</option>
                                </select>
                            </div>

                            {sentence.audioType === 'auto' && (
                                <div className="form-group">
                                    <label>Голос озвучки</label>
                                    <select
                                        value={sentence.voiceId || 'en-US-JennyNeural'}
                                        onChange={(e) => updateStandaloneSentence(index, 'voiceId', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            border: '1px solid #ddd'
                                        }}
                                    >
                                        {availableVoices.map(voice => (
                                            <option key={voice.id} value={voice.id}>
                                                {voice.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {sentence.audioType === 'custom' && (
                                <div className="form-group">
                                    <label>Загрузить аудио файл</label>
                                    {!sentence.audioUrl ? (
                                        <input
                                            type="file"
                                            accept="audio/*"
                                            onChange={(e) => {
                                                if (e.target.files[0]) {
                                                    handleSentenceAudioUpload(index, e.target.files[0]);
                                                }
                                            }}
                                            style={{ marginBottom: '10px' }}
                                        />
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                            <div style={{ 
                                                flex: 1,
                                                padding: '10px',
                                                background: '#f0f9ff',
                                                border: '1px solid #4a90e2',
                                                borderRadius: '6px',
                                                fontSize: '13px',
                                                color: '#4a90e2'
                                            }}>
                                                ✓ Аудио загружено
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveSentenceAudio(index)}
                                                style={{
                                                    padding: '8px 16px',
                                                    background: '#ff4d4f',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px'
                                                }}
                                            >
                                                Удалить
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {sentence.en && (
                                <>
                                    {!isPlayingPreview ? (
                                        <button
                                            type="button"
                                            onClick={() => playPreviewSentence(sentence.en, sentence.voiceId || 'en-US-JennyNeural')}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '10px 20px',
                                                background: '#4a90e2',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#3a7bc8'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = '#4a90e2'}
                                        >
                                            <BsPlayCircle size={18} />
                                            Прослушать {sentence.audioType === 'custom' && sentence.audioUrl ? 'загруженное аудио' : 'с выбранным голосом'}
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={stopPreviewAudio}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '10px 20px',
                                                background: '#ff4d4f',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#d9363e'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = '#ff4d4f'}
                                        >
                                            ⏹
                                            Остановить
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    ))}

                    <button
                        className="btn btn-secondary"
                        onClick={addStandaloneSentence}
                        style={{ marginBottom: '20px' }}
                    >
                        <FiPlus /> Добавить предложение
                    </button>

                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button className="btn" onClick={() => setCurrentStep(1)}>
                            ← Назад
                        </button>
                        <button className="btn btn-primary" onClick={() => setCurrentStep(3)}>
                            Далее: Текст →
                        </button>
                    </div>
                </div>
            )}

            {/* ШАГ 3: Текст */}
            {currentStep === 3 && (
                <div className="card">
                    <h2 style={{ marginBottom: '20px' }}>Шаг 3: Основной текст урока</h2>
                    
                    <div className="form-group">
                        <label>Название текста *</label>
                        <input
                            type="text"
                            value={textTitle}
                            onChange={(e) => setTextTitle(e.target.value)}
                            placeholder="Например: Важное решение"
                        />
                    </div>

                    <div className="form-group">
                        <label>Описание текста</label>
                        <textarea
                            value={textDescription}
                            onChange={(e) => setTextDescription(e.target.value)}
                            placeholder="Краткое описание текста"
                            rows="2"
                        />
                    </div>

                    <div className="form-group">
                        <label>Изображение к тексту (опционально)</label>
                        
                        {!textImageUrl && (
                            <div>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    disabled={uploadingImage}
                                    style={{ marginBottom: '10px' }}
                                />
                                {uploadingImage && <p style={{ color: '#999' }}>Создание превью...</p>}
                                <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                                    Поддерживаемые форматы: JPG, PNG, GIF. Максимум 8 МБ.
                                </small>
                            </div>
                        )}
                        
                        {textImageUrl && (
                            <div>
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '10px',
                                    padding: '10px',
                                    background: '#f5f5f5',
                                    borderRadius: '8px',
                                    marginBottom: '10px'
                                }}>
                                    <img 
                                        src={textImageUrl.startsWith('blob:') ? textImageUrl : `${API_ORIGIN}${textImageUrl}`}
                                        alt="Preview" 
                                        style={{ 
                                            maxWidth: '150px', 
                                            maxHeight: '150px',
                                            borderRadius: '4px',
                                            objectFit: 'cover'
                                        }}
                                        onError={(e) => e.target.style.display = 'none'}
                                    />
                                    <div style={{ flex: 1 }}>
                                        {uploadingImage ? (
                                            <p style={{ fontSize: '13px', color: '#4a90e2', marginBottom: '5px' }}>
                                                ⏳ Загрузка изображения на сервер...
                                            </p>
                                        ) : (
                                            <p style={{ fontSize: '13px', color: '#52c41a', marginBottom: '5px' }}>
                                                ✓ {textImageFile ? 'Загрузка завершена' : 'Изображение загружено'}
                                            </p>
                                        )}
                                        <button 
                                            type="button"
                                            onClick={handleRemoveImage}
                                            className="btn btn-danger"
                                            style={{ fontSize: '13px', padding: '6px 12px' }}
                                            disabled={uploadingImage}
                                        >
                                            <FiTrash2 /> Удалить
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label>Текст на английском *</label>
                        <textarea
                            value={fullTextEn}
                            onChange={(e) => setFullTextEn(e.target.value)}
                            placeholder="Введите полный текст на английском языке&#10;&#10;Используйте | для разделения предложений на смысловые части:&#10;I am going | to the store"
                            rows="10"
                            style={{ fontFamily: 'monospace' }}
                        />
                        <small style={{ color: '#999', display: 'block', marginTop: '5px' }}>
                            Используйте символ <strong>|</strong> внутри предложений для разделения на смысловые части
                        </small>
                    </div>

                    <div className="form-group">
                        <label>Перевод на русский *</label>
                        <textarea
                            value={fullTextRu}
                            onChange={(e) => setFullTextRu(e.target.value)}
                            placeholder="Введите перевод на русский язык&#10;&#10;Важно: ставьте | в тех же местах, что и в английском:&#10;Я иду | в магазин"
                            rows="10"
                            style={{ fontFamily: 'monospace' }}
                        />
                        <small style={{ color: '#999', display: 'block', marginTop: '5px' }}>
                            Ставьте <strong>|</strong> в тех же местах, что и в английском тексте
                        </small>
                    </div>

                    <div className="form-group">
                        <label>Тип озвучки для полного текста</label>
                        <select
                            value={textAudioType}
                            onChange={(e) => setTextAudioType(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '8px',
                                border: '1px solid #ddd',
                                marginBottom: '10px'
                            }}
                        >
                            <option value="auto">Автоматическая озвучка (TTS)</option>
                            <option value="custom">Собственное аудио</option>
                        </select>
                    </div>

                    {textAudioType === 'auto' && (
                        <div className="form-group">
                            <label>Голос озвучки для текста</label>
                            <select
                                value={textVoiceId}
                                onChange={(e) => setTextVoiceId(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd'
                                }}
                            >
                                {availableVoices.map(voice => (
                                    <option key={voice.id} value={voice.id}>
                                        {voice.name}
                                    </option>
                                ))}
                            </select>
                            <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                                Этот голос будет использован для озвучивания всего текста
                            </small>
                            
                            {fullTextEn && (
                                <>
                                    {!isPlayingPreview ? (
                                        <button
                                            type="button"
                                            onClick={() => playPreviewSentence(fullTextEn, textVoiceId)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '10px 20px',
                                                background: '#4a90e2',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                transition: 'all 0.2s',
                                                marginTop: '10px'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#3a7bc8'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = '#4a90e2'}
                                        >
                                            <BsPlayCircle size={18} />
                                            Прослушать текст выбранным голосом
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={stopPreviewAudio}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '10px 20px',
                                                background: '#ff4d4f',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                transition: 'all 0.2s',
                                                marginTop: '10px'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#d9363e'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = '#ff4d4f'}
                                        >
                                            ⏹
                                            Остановить
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {textAudioType === 'custom' && (
                        <div className="form-group">
                            <label>Загрузить аудио для полного текста</label>
                            {!textAudioUrl ? (
                                <div>
                                    <input
                                        type="file"
                                        accept="audio/*"
                                        onChange={handleTextAudioUpload}
                                        disabled={uploadingTextAudio}
                                        style={{ marginBottom: '10px' }}
                                    />
                                    {uploadingTextAudio && <p style={{ color: '#999' }}>Загрузка аудио...</p>}
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                    <div style={{ 
                                        flex: 1,
                                        padding: '10px',
                                        background: '#f0f9ff',
                                        border: '1px solid #4a90e2',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        color: '#4a90e2'
                                    }}>
                                        ✓ Аудио для полного текста загружено
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleRemoveTextAudio}
                                        style={{
                                            padding: '8px 16px',
                                            background: '#ff4d4f',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '13px'
                                        }}
                                    >
                                        Удалить
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    <button
                        className="btn btn-secondary"
                        onClick={splitTextIntoSentences}
                        disabled={!fullTextEn}
                        style={{ marginBottom: '20px' }}
                    >
                        ✂️ Разбить текст на предложения
                    </button>

                    {textSentences.length > 0 && (
                        <div style={{ marginTop: '30px' }}>
                            <h3 style={{ marginBottom: '15px' }}>
                                Предложения текста ({textSentences.length})
                            </h3>
                            <p style={{ color: '#666', marginBottom: '15px', fontSize: '14px' }}>
                                Разделители <strong>|</strong> обрабатываются автоматически. Чтобы изменить разбивку, отредактируйте текст выше.
                            </p>

                            {textSentences.map((sentence, index) => (
                                <div key={index} className="card" style={{ 
                                    marginBottom: '12px', 
                                    background: sentence.partsValid === false ? '#fff3f3' : '#f9f9f9', 
                                    padding: '15px',
                                    border: sentence.partsValid === false ? '1px solid #ff4d4f' : '1px solid #e8e8e8'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                                        <div style={{
                                            minWidth: '32px',
                                            height: '32px',
                                            background: sentence.partsValid === false ? '#ff4d4f' : '#4a90e2',
                                            color: 'white',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: 'bold',
                                            fontSize: '14px'
                                        }}>
                                            {index + 1}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            {/* Английское предложение */}
                                            <div style={{ marginBottom: '8px' }}>
                                                <strong style={{ fontSize: '15px', color: '#333' }}>
                                                    {sentence.en.replace(/\|/g, ' ')}
                                                </strong>
                                            </div>
                                            
                                            {/* Русский перевод */}
                                            {sentence.ru && (
                                                <div style={{ marginBottom: '10px', color: '#666', fontSize: '14px' }}>
                                                    {sentence.ru.replace(/\|/g, ' ')}
                                                </div>
                                            )}
                                            
                                            {/* Выбор типа озвучки для предложения */}
                                            <div style={{ marginTop: '15px', padding: '12px', background: '#fff', borderRadius: '8px', border: '1px solid #e8e8e8' }}>
                                                <div style={{ fontSize: '12px', color: '#999', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    Озвучка предложения:
                                                </div>
                                                <select
                                                    value={sentence.audioType || 'auto'}
                                                    onChange={(e) => {
                                                        const updated = [...textSentences];
                                                        updated[index].audioType = e.target.value;
                                                        setTextSentences(updated);
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #ddd',
                                                        marginBottom: '10px'
                                                    }}
                                                >
                                                    <option value="auto">Автоматическая озвучка (TTS)</option>
                                                    <option value="custom">Собственное аудио</option>
                                                </select>
                                                
                                                {/* Загрузка аудио для целого предложения */}
                                                {sentence.audioType === 'custom' && !sentence.parts?.length && (
                                                    <div>
                                                        {!sentence.audioUrl ? (
                                                            <input
                                                                type="file"
                                                                accept="audio/*"
                                                                onChange={(e) => {
                                                                    if (e.target.files[0]) {
                                                                        handleTextSentenceAudioUpload(index, e.target.files[0]);
                                                                    }
                                                                }}
                                                                style={{ width: '100%' }}
                                                            />
                                                        ) : (
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                <div style={{ 
                                                                    flex: 1,
                                                                    padding: '8px',
                                                                    background: '#e8f5e9',
                                                                    border: '1px solid #81c784',
                                                                    borderRadius: '6px',
                                                                    fontSize: '13px',
                                                                    color: '#2e7d32'
                                                                }}>
                                                                    ✓ Аудио загружено
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const updated = [...textSentences];
                                                                        updated[index].audioUrl = '';
                                                                        updated[index].audioFile = null;
                                                                        setTextSentences(updated);
                                                                    }}
                                                                    style={{
                                                                        padding: '6px 12px',
                                                                        background: '#ff4d4f',
                                                                        color: 'white',
                                                                        border: 'none',
                                                                        borderRadius: '6px',
                                                                        cursor: 'pointer',
                                                                        fontSize: '12px'
                                                                    }}
                                                                >
                                                                    Удалить
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Если есть части и выбрана загрузка - показываем загрузку аудио для каждой части */}
                                            {sentence.audioType === 'custom' && sentence.parts && sentence.parts.length > 1 && sentence.partsValid && (
                                                <div style={{ marginTop: '12px', padding: '12px', background: '#f0f7ff', borderRadius: '6px' }}>
                                                    <div style={{ fontSize: '12px', color: '#4a90e2', marginBottom: '10px', fontWeight: '600' }}>
                                                        Предложение разделено на {sentence.parts.length} части. Загрузите аудио для каждой части:
                                                    </div>
                                                    {sentence.parts.map((part, pIndex) => {
                                                        const partKey = `${index}-${pIndex}`;
                                                        const partAudio = sentence.partsAudio?.[pIndex];
                                                        
                                                        return (
                                                            <div key={pIndex} style={{
                                                                marginBottom: '10px',
                                                                padding: '10px',
                                                                background: 'white',
                                                                borderRadius: '6px',
                                                                border: '1px solid #d4e6f7'
                                                            }}>
                                                                <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '8px', color: '#2c3e50' }}>
                                                                    Часть {pIndex + 1}: "{part}" / "{sentence.partsRu?.[pIndex] || ''}"
                                                                </div>
                                                                
                                                                {!partAudio?.audioUrl ? (
                                                                    <input
                                                                        type="file"
                                                                        accept="audio/*"
                                                                        onChange={(e) => {
                                                                            if (e.target.files[0]) {
                                                                                handleTextSentencePartAudioUpload(index, pIndex, e.target.files[0]);
                                                                            }
                                                                        }}
                                                                        style={{ fontSize: '12px' }}
                                                                    />
                                                                ) : (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        <div style={{ 
                                                                            flex: 1,
                                                                            padding: '6px',
                                                                            background: '#e8f5e9',
                                                                            border: '1px solid #81c784',
                                                                            borderRadius: '4px',
                                                                            fontSize: '12px',
                                                                            color: '#2e7d32'
                                                                        }}>
                                                                            ✓ Аудио загружено
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                const updated = [...textSentences];
                                                                                if (!updated[index].partsAudio) updated[index].partsAudio = [];
                                                                                updated[index].partsAudio[pIndex] = null;
                                                                                setTextSentences(updated);
                                                                            }}
                                                                            style={{
                                                                                padding: '4px 10px',
                                                                                background: '#ff4d4f',
                                                                                color: 'white',
                                                                                border: 'none',
                                                                                borderRadius: '4px',
                                                                                cursor: 'pointer',
                                                                                fontSize: '11px'
                                                                            }}
                                                                        >
                                                                            Удалить
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            
                                            {/* Предупреждение о несовпадении */}
                                            {sentence.partsValid === false && (
                                                <div style={{
                                                    marginTop: '10px',
                                                    padding: '8px 12px',
                                                    background: '#fff',
                                                    border: '1px solid #ff4d4f',
                                                    borderRadius: '6px',
                                                    fontSize: '13px',
                                                    color: '#ff4d4f'
                                                }}>
                                                    ⚠️ Количество разделителей | не совпадает в английском и русском тексте!
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                        <button className="btn" onClick={() => setCurrentStep(2)}>
                            ← Назад
                        </button>
                        <button 
                            className="btn btn-primary" 
                            onClick={() => setCurrentStep(4)}
                            disabled={!canProceedToStep5}
                        >
                            Далее: Упражнения →
                        </button>
                    </div>
                </div>
            )}

            {/* ШАГ 4: Упражнения */}
            {currentStep === 4 && (
                <div className="card">
                    <h2 style={{ marginBottom: '20px' }}>Шаг 4: Упражнения к уроку</h2>
                    <p style={{ color: '#666', marginBottom: '20px' }}>
                        Добавьте различные типы упражнений для практики
                    </p>

                    {exercises.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', background: '#f9f9f9', borderRadius: '8px', marginBottom: '20px' }}>
                            <p style={{ color: '#999', marginBottom: '20px' }}>Упражнения еще не добавлены</p>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                <button className="btn btn-secondary" onClick={() => addExercise('multiple_choice')}>
                                    <FiCheckCircle /> Тест (выбор ответа)
                                </button>
                                <button className="btn btn-secondary" onClick={() => addExercise('true_false')}>
                                    <FiCheckCircle /> Правда/Ложь
                                </button>
                                <button className="btn btn-secondary" onClick={() => addExercise('fill_gaps')}>
                                    <FiEdit3 /> Заполнить пропуски
                                </button>
                                <button className="btn btn-secondary" onClick={() => addExercise('text_answer')}>
                                    <FiFileText /> Текстовый ответ
                                </button>
                                <button className="btn btn-secondary" onClick={() => addExercise('match')}>
                                    <FiList /> Сопоставление
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {exercises.map((exercise, exIndex) => (
                                <div key={exIndex} className="card" style={{ marginBottom: '20px', background: '#f9f9f9' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                        <h3 style={{ margin: 0 }}>
                                            Упражнение {exIndex + 1}: {
                                                exercise.type === 'multiple_choice' ? <><FiCheckCircle style={{ verticalAlign: 'middle' }} /> Тест</> :
                                                exercise.type === 'true_false' ? <><FiCheckCircle style={{ verticalAlign: 'middle' }} /> Правда/Ложь</> :
                                                exercise.type === 'fill_gaps' ? <><FiEdit3 style={{ verticalAlign: 'middle' }} /> Заполнить пропуски</> :
                                                exercise.type === 'text_answer' ? <><FiFileText style={{ verticalAlign: 'middle' }} /> Текстовый ответ</> :
                                                <><FiList style={{ verticalAlign: 'middle' }} /> Сопоставление</>
                                            }
                                        </h3>
                                        <button
                                            className="btn-icon btn-danger"
                                            onClick={() => removeExercise(exIndex)}
                                        >
                                            <FiTrash2 />
                                        </button>
                                    </div>

                                    <div className="form-group">
                                        <label>Название упражнения</label>
                                        <input
                                            type="text"
                                            value={exercise.title}
                                            onChange={(e) => updateExercise(exIndex, 'title', e.target.value)}
                                            placeholder="Например: Проверка понимания текста"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Инструкции</label>
                                        <textarea
                                            value={exercise.instructions}
                                            onChange={(e) => updateExercise(exIndex, 'instructions', e.target.value)}
                                            placeholder="Инструкции для выполнения"
                                            rows="2"
                                        />
                                    </div>

                                    <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Вопросы:</h4>

                                    {exercise.questions.map((question, qIndex) => (
                                        <div key={qIndex} style={{ marginBottom: '15px', padding: '15px', background: 'white', borderRadius: '8px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                                <strong>Вопрос {qIndex + 1}</strong>
                                                {exercise.questions.length > 1 && (
                                                    <button
                                                        className="btn-icon btn-danger"
                                                        onClick={() => {
                                                            const updated = [...exercises];
                                                            updated[exIndex].questions = updated[exIndex].questions.filter((_, i) => i !== qIndex);
                                                            setExercises(updated);
                                                        }}
                                                        style={{ fontSize: '12px', padding: '4px 8px' }}
                                                    >
                                                        <FiTrash2 size={14} />
                                                    </button>
                                                )}
                                            </div>

                                            <div className="form-group">
                                                <label>Текст вопроса *</label>
                                                <input
                                                    type="text"
                                                    value={question.questionText}
                                                    onChange={(e) => updateQuestion(exIndex, qIndex, 'questionText', e.target.value)}
                                                    placeholder="Введите вопрос"
                                                />
                                            </div>

                                            {exercise.type === 'multiple_choice' && (
                                                <>
                                                    <div className="form-group">
                                                        <label>Варианты ответов (через запятую)</label>
                                                        <input
                                                            type="text"
                                                            value={question.options ? question.options.join(', ') : ''}
                                                            onChange={(e) => {
                                                                const options = e.target.value.split(',').map(o => o.trim()).filter(o => o);
                                                                updateQuestion(exIndex, qIndex, 'options', options);
                                                            }}
                                                            placeholder="вариант1, вариант2, вариант3, вариант4"
                                                        />
                                                        {question.options && question.options.length > 0 && (
                                                            <small style={{ color: '#52c41a' }}>
                                                                ✓ {question.options.length} вариантов
                                                            </small>
                                                        )}
                                                    </div>

                                                    <div className="form-group">
                                                        <label>Правильный ответ</label>
                                                        <select
                                                            value={question.correctAnswer}
                                                            onChange={(e) => updateQuestion(exIndex, qIndex, 'correctAnswer', e.target.value)}
                                                        >
                                                            <option value="">Выберите правильный ответ</option>
                                                            {question.options && question.options.map((opt, i) => (
                                                                <option key={i} value={opt}>{opt}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </>
                                            )}

                                            {exercise.type === 'true_false' && (
                                                <div className="form-group">
                                                    <label>Правильный ответ</label>
                                                    <select
                                                        value={question.correctAnswer}
                                                        onChange={(e) => updateQuestion(exIndex, qIndex, 'correctAnswer', e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            padding: '10px',
                                                            borderRadius: '8px',
                                                            border: '1px solid #ddd',
                                                            fontSize: '14px'
                                                        }}
                                                    >
                                                        <option value="">Выберите ответ</option>
                                                        <option value="true">✓ Правда (True)</option>
                                                        <option value="false">✗ Ложь (False)</option>
                                                    </select>
                                                </div>
                                            )}

                                            {exercise.type === 'text_answer' && (
                                                <div className="form-group">
                                                    <label>Правильный ответ (или ключевые слова)</label>
                                                    <input
                                                        type="text"
                                                        value={question.correctAnswer}
                                                        onChange={(e) => updateQuestion(exIndex, qIndex, 'correctAnswer', e.target.value)}
                                                        placeholder="Введите правильный ответ"
                                                    />
                                                </div>
                                            )}

                                            {exercise.type === 'fill_gaps' && (
                                                <div className="form-group">
                                                    <label>Правильный ответ (слово для пропуска)</label>
                                                    <input
                                                        type="text"
                                                        value={question.correctAnswer}
                                                        onChange={(e) => updateQuestion(exIndex, qIndex, 'correctAnswer', e.target.value)}
                                                        placeholder="Введите слово"
                                                    />
                                                </div>
                                            )}

                                            {exercise.type === 'match' && (
                                                <>
                                                    <div className="form-group">
                                                        <label>Пары для сопоставления (каждая пара через |, пары через запятую)</label>
                                                        <input
                                                            type="text"
                                                            value={question.matchPairs ? question.matchPairs.map(p => `${p.left}|${p.right}`).join(', ') : ''}
                                                            onChange={(e) => {
                                                                const inputValue = e.target.value;
                                                                // Парсим только если есть разделители
                                                                if (inputValue.includes('|')) {
                                                                    const pairs = inputValue.split(',').map(pair => {
                                                                        const [left, right] = pair.split('|').map(s => s.trim());
                                                                        return { left: left || '', right: right || '' };
                                                                    });
                                                                    updateQuestion(exIndex, qIndex, 'matchPairs', pairs);
                                                                } else {
                                                                    // Если нет разделителей, сохраняем как есть (для удобства ввода)
                                                                    updateQuestion(exIndex, qIndex, 'matchPairs', [{ left: inputValue, right: '' }]);
                                                                }
                                                            }}
                                                            placeholder="слово1|перевод1, слово2|перевод2"
                                                        />
                                                        {question.matchPairs && question.matchPairs.length > 0 && question.matchPairs[0].left && question.matchPairs[0].right && (
                                                            <small style={{ color: '#52c41a' }}>
                                                                ✓ {question.matchPairs.filter(p => p.left && p.right).length} пар для сопоставления
                                                            </small>
                                                        )}
                                                    </div>
                                                </>
                                            )}

                                            <div className="form-group">
                                                <label>Объяснение (показывается после ответа)</label>
                                                <textarea
                                                    value={question.explanation}
                                                    onChange={(e) => updateQuestion(exIndex, qIndex, 'explanation', e.target.value)}
                                                    placeholder="Объясните правильный ответ"
                                                    rows="2"
                                                />
                                            </div>
                                        </div>
                                    ))}

                                    <button
                                        className="btn btn-secondary"
                                        onClick={() => addQuestionToExercise(exIndex)}
                                        style={{ fontSize: '13px' }}
                                    >
                                        <FiPlus /> Добавить вопрос
                                    </button>
                                </div>
                            ))}

                            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
                                <button className="btn btn-secondary" onClick={() => addExercise('multiple_choice')}>
                                    <FiPlus /> Тест
                                </button>
                                <button className="btn btn-secondary" onClick={() => addExercise('true_false')}>
                                    <FiPlus /> Правда/Ложь
                                </button>
                                <button className="btn btn-secondary" onClick={() => addExercise('fill_gaps')}>
                                    <FiPlus /> Заполнить пропуски
                                </button>
                                <button className="btn btn-secondary" onClick={() => addExercise('text_answer')}>
                                    <FiPlus /> Текстовый ответ
                                </button>
                                <button className="btn btn-secondary" onClick={() => addExercise('match')}>
                                    <FiPlus /> Сопоставление
                                </button>
                            </div>
                        </>
                    )}

                    <div style={{ display: 'flex', gap: '10px', marginTop: '30px' }}>
                        <button className="btn" onClick={() => setCurrentStep(3)}>
                            ← Назад
                        </button>
                        <button className="btn btn-primary" onClick={() => setCurrentStep(5)}>
                            Далее: Видео →
                        </button>
                    </div>
                </div>
            )}

            {/* ШАГ 5: Видео */}
            {currentStep === 5 && (
                <div className="card">
                    <h2 style={{ marginBottom: '20px' }}>Шаг 5: Видео (опционально)</h2>
                    <p style={{ color: '#666', marginBottom: '20px' }}>
                        Добавьте видео к уроку (YouTube, Vimeo или загруженные)
                    </p>

                    {videos.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', background: '#f9f9f9', borderRadius: '8px', marginBottom: '20px' }}>
                            <p style={{ color: '#999', marginBottom: '20px' }}>Видео не добавлены</p>
                            <button className="btn btn-secondary" onClick={addVideo}>
                                <FiPlus /> Добавить видео
                            </button>
                        </div>
                    ) : (
                        <>
                            {videos.map((video, index) => (
                                <div key={index} className="card" style={{ marginBottom: '15px', background: '#f9f9f9' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                        <strong>Видео {index + 1}</strong>
                                        <button
                                            className="btn-icon btn-danger"
                                            onClick={() => removeVideo(index)}
                                        >
                                            <FiTrash2 />
                                        </button>
                                    </div>

                                    <div className="form-group">
                                        <label>Название видео</label>
                                        <input
                                            type="text"
                                            value={video.title}
                                            onChange={(e) => updateVideo(index, 'title', e.target.value)}
                                            placeholder="Название видео"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Тип видео</label>
                                        <select
                                            value={video.type}
                                            onChange={(e) => updateVideo(index, 'type', e.target.value)}
                                        >
                                            <option value="youtube">YouTube</option>
                                            <option value="vimeo">Vimeo</option>
                                            <option value="upload">Загруженное видео</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>URL видео *</label>
                                        <input
                                            type="text"
                                            value={video.url}
                                            onChange={(e) => updateVideo(index, 'url', e.target.value)}
                                            placeholder={
                                                video.type === 'youtube' ? 'https://www.youtube.com/watch?v=...' :
                                                video.type === 'vimeo' ? 'https://vimeo.com/...' :
                                                '/uploads/videos/...'
                                            }
                                        />
                                    </div>
                                </div>
                            ))}

                            <button className="btn btn-secondary" onClick={addVideo}>
                                <FiPlus /> Добавить еще видео
                            </button>
                        </>
                    )}

                    <div style={{ display: 'flex', gap: '10px', marginTop: '30px' }}>
                        <button className="btn" onClick={() => setCurrentStep(4)}>
                            ← Назад
                        </button>
                        <button className="btn btn-primary" onClick={() => setCurrentStep(6)}>
                            Далее: Сохранить →
                        </button>
                    </div>
                </div>
            )}

            {/* ШАГ 6: Сохранение */}
            {currentStep === 6 && (
                <div className="card">
                    <h2 style={{ marginBottom: '20px' }}>Шаг 6: Проверка и сохранение</h2>
                    
                    <div style={{ background: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                        <h3 style={{ marginBottom: '15px' }}>Сводка урока:</h3>
                        
                        <div style={{ marginBottom: '10px' }}>
                            <strong>Категория:</strong> {category === 'grammar' ? 'Грамматика' : 'Лексика'}
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <strong>Название:</strong> {lessonTitle}
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <strong>Отдельных предложений:</strong> {standaloneSentences.filter(s => s.en).length}
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <strong>Текст:</strong> {textTitle ? `"${textTitle}" (${textSentences.length} предложений)` : 'Не добавлен'}
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <strong>Упражнений:</strong> {exercises.length}
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <strong>Видео:</strong> {videos.length}
                        </div>
                    </div>

                    {!canProceedToStep2 && (
                        <div className="error-message">
                            Заполните обязательные поля: область, уровень и название урока
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button className="btn" onClick={() => setCurrentStep(5)}>
                            ← Назад
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={handleSaveLesson}
                            disabled={loading || !canProceedToStep2}
                            style={{ flex: 1, fontSize: '16px', padding: '14px' }}
                        >
                            <FiSave /> {loading ? 'Сохранение...' : 'Сохранить урок'}
                        </button>
                    </div>
                </div>
            )}

            {/* Продолжение в следующем сообщении... */}
        </div>
    );
};

export default CreateLessonPage;
